import { useNavigate, useRouterState } from '@tanstack/react-router';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Focusable, Selection } from 'react-aria-components';

import { Editable, Flex, Heading, Menu, MenuItem, Save, Text, Tooltip, TooltipTrigger } from '@/components/core';
import {
  ChatIcon,
  ChevronSelectorIcon,
  InsightsIcon,
  OverflowIcon,
  RunsIcon,
  ShareFeedbackIcon,
  WarningDuotoneIcon,
  WorkflowIcon,
} from '@/components/icons';
import { NavigationBar as NavBar } from '@/components/ui';
import { DefaultPermissions } from '@/constants';
import {
  useAgentIcon,
  useDebounceCallback,
  useGetAccessLevel,
  useGetAgentFromPath,
  useGetNotConfiguredExports,
  useRemainingDaysOfTrial,
  useTour,
} from '@/hooks';
import { toAgentId, toModelId, useGetModel, useUpdateAgent } from '@/hooks/api';
import { AccessLevel, findModelId } from '@/utils';

import { AgentOptionsMenu } from './AgentOptionsMenu';
import { AgentSelectorMenu } from './AgentSelectorMenu';
import { UpgradeCard } from './UpgradeCard';
import { WorkspaceCard } from './WorkspaceCard';

const INavigationBar = () => {
  const { data: agent } = useGetAgentFromPath();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const { actions } = useGetNotConfiguredExports({ agentId: agent?.agentId });
  const { data: model } = useGetModel(toModelId(findModelId(agent)));
  const { showReviewPredictionsTooltip } = useTour();

  const { mutateAsync: updateAgent } = useUpdateAgent();
  const { hasAccessLevel, hasPermission } = useGetAccessLevel();

  const onChangeAgentName = useDebounceCallback(
    useCallback(
      (name: string) => {
        const agentId = toAgentId(agent?.agentId);
        if (agentId) {
          updateAgent({ id: agentId, updates: { name } });
        }
      },
      [agent?.agentId, updateAgent]
    ),
    300
  );

  const routeHandler = useMemo(() => {
    if (!agent) return;

    const runsRoute = { to: '/agents/$agentId/inbox', params: { agentId: agent.agentId } };
    const workflowRoute = { to: '/agents/$agentId/integration', params: { agentId: agent.agentId } };
    const insightsRoute = { to: '/agents/$agentId/analytics', params: { agentId: agent.agentId } };

    return {
      runs: {
        id: 'runs',
        navigate: async () => navigate(runsRoute),
        isSelected: routerState.location.pathname.endsWith('inbox'),
      },
      workflow: {
        id: 'workflow',
        navigate: async () => navigate(workflowRoute),
        isSelected: routerState.location.pathname.endsWith('integration'),
      },
      insights: {
        id: 'insights',
        navigate: async () => navigate(insightsRoute),
        isSelected: routerState.location.pathname.endsWith('analytics'),
      },
    };
  }, [navigate, agent, routerState.location.pathname]);

  const selectedKeysFromRouteHandler = useMemo(
    () =>
      Object.values(routeHandler ?? {})
        .filter((route) => route.isSelected)
        .map((route) => route.id),
    [routeHandler]
  );

  const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set(selectedKeysFromRouteHandler));

  useEffect(() => {
    setSelectedKeys(new Set(selectedKeysFromRouteHandler));
  }, [selectedKeysFromRouteHandler]);

  const { hasUpgraded } = useRemainingDaysOfTrial();

  const agentIcon = useAgentIcon({ agentId: agent?.agentId, className: 'size-5' });

  if (!agent || !routeHandler) {
    return <Save message="Loading agent ..." />;
  }

  const flowSetupRequired =
    !showReviewPredictionsTooltip && (actions.length || Object.entries(model?.fieldConfig ?? {}).length < 1);

  const canCreateAgents = hasPermission(DefaultPermissions.AllAgentsWrite);

  return (
    <NavBar>
      <WorkspaceCard className="h-12 border-b-1 border-gray-200" organizationId="me" />

      <div className="flex flex-col gap-2 p-2">
        <Flex variant="secondary" className="group/agent-card items-center rounded-lg pr-1 pl-2 shadow-xs">
          {agentIcon}
          <Editable key={agent.agentId} label="Edit agent name" onChange={onChangeAgentName} defaultValue={agent.name}>
            <Heading size="sm">{agent.name}</Heading>
          </Editable>

          <div className="grow" />

          {canCreateAgents && (
            <AgentOptionsMenu className="invisible group-hover/agent-card:visible">
              <OverflowIcon />
            </AgentOptionsMenu>
          )}

          <AgentSelectorMenu>
            <ChevronSelectorIcon />
          </AgentSelectorMenu>
        </Flex>

        <Menu className="gap-xs flex flex-col" selectionMode="single" selectedKeys={selectedKeys} aria-label="Main">
          <MenuItem id={routeHandler.runs.id} onAction={routeHandler.runs.navigate} aria-label="Runs">
            <RunsIcon />
            <Text>Runs</Text>
          </MenuItem>
          {hasAccessLevel(AccessLevel.Developer) && (
            <MenuItem id={routeHandler.workflow.id} onAction={routeHandler.workflow.navigate} aria-label="Workflow">
              <WorkflowIcon />
              <Text>Workflow</Text>
              {flowSetupRequired ? (
                <>
                  <div className="grow" />
                  <TooltipTrigger>
                    <Tooltip className="flex flex-col" variant="primary">
                      <Heading>Setup needed</Heading>
                      <Text>Configure your integration to export files automatically.</Text>
                    </Tooltip>
                    <Focusable>
                      <WarningDuotoneIcon role="button" variant="warning" />
                    </Focusable>
                  </TooltipTrigger>
                </>
              ) : null}
            </MenuItem>
          )}
          <MenuItem id={routeHandler.insights.id} onAction={routeHandler.insights.navigate} aria-label="Insights">
            <InsightsIcon />
            <Text>Insights</Text>
          </MenuItem>
        </Menu>
      </div>

      <div className="grow" />

      <div className="p-2">
        <Menu>
          <MenuItem onAction={() => window.open('https://tally.so/r/3qkeN5', '_blank')}>
            <ShareFeedbackIcon />
            <Text>Share feedback</Text>
          </MenuItem>
          <MenuItem onAction={() => window._chatlio?.open()}>
            <ChatIcon />
            <Text>Chat with an expert</Text>
          </MenuItem>
        </Menu>
      </div>

      {!hasUpgraded && <UpgradeCard organizationId="me" />}

      <Text className="mb-2 self-end px-4 text-gray-300">
        version: {(import.meta.env.VITE_CURRENT_COMMIT_SHA ?? '').substring(0, 7) || 'dev'}
      </Text>
    </NavBar>
  );
};

export const NavigationBar = memo(INavigationBar);
